import { createCalendar } from '@internationalized/date';
import type { AriaDatePickerProps, DateValue } from '@react-aria/datepicker';
import { clsx } from 'clsx';
import { forwardRef, useEffect, useRef } from 'react';
import { useDateField } from 'react-aria';
import { useDateFieldState } from 'react-stately';
import { spacing } from '../../../styles/spacing.css';
import { themeVars } from '../../../styles/theme.contract.css';
import { ellipsis, floatingLabel } from '../../../styles/utilities.css';
import { Flex } from '../../Flex/Flex';
import { Layout } from '../../Layout/Layout';
import { Text } from '../../Text/Text';
import * as styles from './DateField.css';
import { DateSegment } from './DateSegment';

interface DateFieldProps extends AriaDatePickerProps<DateValue> {
  readonly locale?: string;
  readonly dateValue: DateValue | null;
  readonly placeholder?: string;
  readonly showDateContent?: boolean;
  readonly disabled?: boolean;
  readonly onSegmentClick?: (event: React.MouseEvent<HTMLParagraphElement>) => void;
  readonly onSegmentKeyPress?: (event: React.KeyboardEvent<HTMLParagraphElement>) => void;
  readonly hasError?: boolean;
  readonly isBlurred?: boolean;
}

export const DateField = forwardRef<HTMLDivElement, DateFieldProps>(
  (
    {
      showDateContent,
      dateValue,
      locale = 'en-US',
      placeholder,
      onSegmentKeyPress,
      onSegmentClick,
      disabled,
      hasError,
      isBlurred,
      ...props
    },
    dateFieldRef,
  ) => {
    const state = useDateFieldState({
      ...props,
      value: dateValue,
      locale,
      createCalendar,
      isDisabled: disabled,
      shouldForceLeadingZeros: true,
    });

    const ref = useRef<HTMLDivElement>(null);
    const { labelProps, fieldProps } = useDateField(props, state, ref);

    useEffect(() => {
      if (!isBlurred && showDateContent && state.segments.length > 0) {
        const firstSegment = ref.current?.querySelector('[role="spinbutton"]') as HTMLDivElement | null;
        firstSegment?.focus({ preventScroll: true });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDateContent, isBlurred]);

    const placeholderColor = hasError
      ? themeVars.color.textNegative
      : disabled
        ? themeVars.color.textDisabled
        : themeVars.color.textTertiary;

    return (
      <Layout
        color={disabled ? themeVars.color.textDisabled : themeVars.color.textPrimary}
        data-private
        position="relative"
        ref={dateFieldRef}
        top={showDateContent ? spacing.s08 : 0}
        width="100%"
      >
        <Text
          className={clsx(floatingLabel, ellipsis, {
            [styles.focusDateFieldFloatingLabel]: showDateContent,
          })}
          color={placeholderColor}
          textAlign="left"
          top={`-${spacing.s12}`}
          type={hasError ? 'h5' : 'bodyMedium'}
          {...labelProps}
        >
          {placeholder}
        </Text>
        {showDateContent && (
          <Flex as="div" {...fieldProps} onClick={onSegmentClick} ref={ref} width="fit-content">
            {state.segments.map((segment, i) => (
              <DateSegment
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                onSegmentKeyPress={onSegmentKeyPress}
                segment={segment}
                shouldFocus={!isBlurred && i === 0}
                state={state}
              />
            ))}
          </Flex>
        )}
      </Layout>
    );
  },
);
